// components/RightColumn.jsx
// Created: 2026-03-11 15:00:00 EST
// Updated: 2026-03-13 14:56:26 EDT
// Project: Giving Words to Voices
// Author: Michael Schwarz
// Version: 1.0.5
//
// Main editor sidebar.
// Memoised so it only re-renders when marks/theme/filters change,
// NOT on every App state change (timecode, waveform, video loading, etc.)
//
// NOTE: cleanLabel() is called in the preview table — this must either be
// imported from a utils file or passed as a prop. Currently expects it to be
// passed via props or available in scope. TODO: move to utils/stringUtils.js

import React from 'react';
import axios from 'axios';
import { ArrowRight, AlertTriangle, BookOpen, Check, CheckSquare, ChevronDown, ChevronUp, DownloadCloud, FileEdit, Film, Folder, Globe, History, Loader, Lock, PenLine, RefreshCw, RotateCcw, Search, Sparkles, Trash2, X } from 'lucide-react';
import { Icon } from './Icon';
import { contrastText } from '../constants/theme';
import { speakerColor, isDarkTheme } from '../constants/tokens';
import MarkCard from './MarkCard';
import LanguageSelector from './LanguageSelector';
import LoadFailure from './LoadFailure';
import Button from './ui/Button';
import Input from './ui/Input';
import { getLanguageCode, getLanguageMeta, normalizeLanguageKey } from '../constants/languages';
import { useConfirm } from '../confirmContext';

function GlossaryAddRule({ theme, BACKEND_URL, currentRoot, refreshGlossary, t = k => k }) {
  const [mis, setMis] = React.useState('');
  const [cor, setCor] = React.useState('');
  const [scope, setScope] = React.useState('project');
  const accent = theme.glossaryAccent || '#6366f1';
  return (
    <div style={{ background: theme.card, padding: '8px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <Input theme={theme} placeholder={t('glossary.errorMisspelling')} value={mis} onChange={e => setMis(e.target.value)} style={{ fontSize: '0.72rem' }} />
      <Input theme={theme} placeholder={t('glossary.correctionFix')} value={cor} onChange={e => setCor(e.target.value)} style={{ fontSize: '0.72rem' }} />
      <div style={{ display: 'flex', gap: '4px' }}>
        {['project', 'global'].map(s => {
          const on = scope === s;
          const scopeBg = on ? (s === 'global' ? (theme.accent || '#ef4444') : accent) : (theme.inputBg || theme.card);
          return (
          <button key={s} onClick={() => setScope(s)} style={{
            flex: 1, padding: '4px', fontSize: '0.62rem', fontWeight: 700,
            background: scopeBg,
            color: on ? contrastText(scopeBg) : theme.textSub,
            border: `1px solid ${on ? 'transparent' : theme.border}`,
            borderRadius: '5px', cursor: 'pointer', textTransform: 'uppercase',
          }}>{s === 'project' ? t('glossary.thisProject') : t('glossary.global')}</button>
          );
        })}
      </div>
      <button
        disabled={!mis.trim() || !cor.trim()}
        onClick={() => {
          const root = scope === 'project' ? currentRoot : '';
          axios.post(`${BACKEND_URL}/glossary`, { misspelling: mis, correction: cor, project_root: root })
            .then(() => { refreshGlossary(); setMis(''); setCor(''); });
        }}
        style={{ padding: '7px', background: accent, color: contrastText(accent), border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer', fontSize: '0.68rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', opacity: (!mis.trim() || !cor.trim()) ? 0.5 : 1 }}
      >
        <Icon icon={Check} size={11} /> {t('glossary.addRule')}
      </button>
    </div>
  );
}

function SpeakerLegend({ speakerMap, canEdit, theme, inputStyle, onRename, onSwap, onDownload }) {
  const [editing, setEditing] = React.useState(null);
  const list = Object.values(speakerMap || {}).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  if (list.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', margin: '2px 0 10px', padding: '6px 8px', background: theme.sidebar, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
      <span style={{ fontSize: theme.fsBadge, fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Speakers</span>
      {list.map(spk => {
        const col = spk.color || theme.accent || '#6b7280';
        const isEd = editing === spk.cluster_id;
        return (
          <div key={spk.cluster_id} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: col + '1a', border: `1px solid ${col}55`, borderRadius: '10px', padding: '2px 8px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: col, flexShrink: 0 }} />
            {isEd ? (
              <input
                autoFocus
                defaultValue={spk.label || ''}
                onKeyDown={e => { if (e.key === 'Enter') { onRename(spk.cluster_id, e.target.value); setEditing(null); } else if (e.key === 'Escape') setEditing(null); }}
                onBlur={e => { onRename(spk.cluster_id, e.target.value); setEditing(null); }}
                style={{ ...inputStyle, width: '120px', fontSize: theme.fsBadge, padding: '1px 6px' }}
              />
            ) : (
              <>
                <span style={{ fontSize: theme.fsBadge, fontWeight: 700, color: col }}>{spk.label || spk.cluster_id}</span>
                {canEdit && (
                  <button
                    title="Rename speaker"
                    onClick={() => setEditing(spk.cluster_id)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: col, display: 'flex', padding: 0 }}
                  >
                    <Icon icon={PenLine} size={11} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}
      {canEdit && onSwap && list.length === 2 && (
        <button
          title="Swap the two speakers (use if the Interviewer / Interviewee guess is backwards)"
          onClick={onSwap}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', color: theme.textSub, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '2px 8px', cursor: 'pointer', fontSize: theme.fsBadge, fontWeight: 700 }}
        >
          <Icon icon={RefreshCw} size={11} /> Swap
        </button>
      )}
      {onDownload && (
        <button
          title="Download this transcript as WebVTT with speaker labels (<v Speaker> tags)"
          onClick={onDownload}
          style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', color: theme.textSub, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '2px 8px', cursor: 'pointer', fontSize: theme.fsBadge, fontWeight: 700 }}
        >
          <Icon icon={DownloadCloud} size={11} /> VTT
        </button>
      )}
    </div>
  );
}

const RightColumn = React.memo(function RightColumn({
  marks, marksLoading, selectedVideo, selectedPart, theme, inputStyle, formatVTTTime,
  BACKEND_URL, videoRef, onRefresh, onCompleteAllPart, getProjectRoot, cleanLabel,
  availableLanguages = [], preferredEditingLanguage = '', sourceLanguage = '',
  vttSourceLanguage = '',
  userRole = '',
  allowedParts = null,
  taskAssignments = [],
  currentUserId = null,
  pushToast = () => {},
  glossary = [], glossarySearch = '', setGlossarySearch = () => {},
  processedGlossary = [], refreshGlossary = () => {}, refreshMarks = () => {},
  sortConfig = { key: 'misspelling', direction: 'asc' }, setSortConfig = () => {},
  t = (k) => k,
  onSwitchTape = () => {},
  vttVersions = [],
  reportLangs = [],
  onDownloadAllParts = null,
  onWriteCorrected = null,
  onRefreshAllVTT = null,
  onRestoreVersion = null,
  subjectVttLangs = [],
  translatingLangs = new Set(),
  onTranslateLanguage = null,
  onRebuildAllTranslations = null,
  rebuildingTranslations = false,
}) {
  const confirm = useConfirm();
  const roleCanEdit = userRole === 'admin' || userRole === 'editor';
  const partAllowed = !allowedParts || allowedParts.includes(selectedPart);
  const canEdit = roleCanEdit && partAllowed;

  // Timecode-range check for granular task assignments
  const hasRangeAssignments = taskAssignments.length > 0;
  const currentPartRanges = React.useMemo(() => {
    if (!hasRangeAssignments) return null;
    return taskAssignments.filter(a => a.part_number === null || a.part_number === selectedPart);
  }, [taskAssignments, selectedPart, hasRangeAssignments]);

  const isInAssignedRange = React.useCallback((timestamp) => {
    if (!hasRangeAssignments || !currentPartRanges) return true;
    if (currentPartRanges.length === 0) return false;
    return currentPartRanges.some(a =>
      (a.tc_start === null || a.tc_start === undefined || timestamp >= a.tc_start) &&
      (a.tc_end === null || a.tc_end === undefined || timestamp <= a.tc_end)
    );
  }, [hasRangeAssignments, currentPartRanges]);
  const [searchTerm, setSearchTerm]         = React.useState('');
  const [editingLanguage, setEditingLanguage] = React.useState('');
  const [visibleLanguages, setVisibleLanguages] = React.useState([]);

  // Unified view state
  const [unifiedMode, setUnifiedMode]         = React.useState(true);
  const [unifiedBlocks, setUnifiedBlocks]     = React.useState([]);
  // Distinct colour per speaker, in TWO mode-aware palettes so the pill label
  // stays legible on both light and dark. Palette + mode logic now live in the
  // token contract (constants/tokens.js) as the single source; a colour set on
  // the speaker row itself always wins. See speakerColor / isDarkTheme there.
  const isDark = React.useMemo(() => isDarkTheme(theme), [theme]);
  const [speakerRows, setSpeakerRows]         = React.useState([]); // raw rows from the server
  const speakerMap = React.useMemo(() => {
    const map = {};
    (speakerRows || []).forEach((sp, i) => {
      if (sp && sp.cluster_id) map[sp.cluster_id] = { ...sp, color: sp.color || speakerColor(i, isDark) };
    });
    return map;
  }, [speakerRows, isDark]);
  const [cueWords, setCueWords]               = React.useState({}); // {cueStart2dp: {cue_start,straddles,words[]}}
  const [unifiedLoading, setUnifiedLoading]   = React.useState(false);
  // Distinct from "loaded and empty" — see loadUnified.
  const [unifiedError, setUnifiedError]       = React.useState(false);
  // A cue's identity, independent of where it happens to sit in the rendered
  // list. Not block.id — non-mark blocks have none, which is why the render key
  // falls back to the index. Part plus start time is unique and stable.
  const blockKey = React.useCallback(
    (b) => `${b?.part_number ?? 0}@${b?.timestamp_start}`, []);
  const [activeBlockKey, setActiveBlockKey]   = React.useState(null);
  const [unifiedFilter, setUnifiedFilter]     = React.useState('all'); // 'all' | 'edited'
  // Compare overlay: show the second engine's speaker instead of the primary.
  // Read-only by design — it exists to point a human at cues worth listening to,
  // not to be trusted as an attribution.
  const [speakerCompareMode, setSpeakerCompareMode] = React.useState(false);
  const [searchResults, setSearchResults]     = React.useState(null); // null = not searching, [] = no results

  // Panel view: 'unified' | 'history' | 'cleanup'
  const [panelView, setPanelView]         = React.useState('unified');
  const historyOpen = panelView === 'history';

  // Edit history
  const [historyItems, setHistoryItems]   = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyFieldFilter, setHistoryFieldFilter] = React.useState('all');

  // Cleanup state
  const [cleanupData, setCleanupData]       = React.useState(null);
  const [cleanupLoading, setCleanupLoading] = React.useState(false);
  const [cleanupRunning, setCleanupRunning] = React.useState(false);

  const loadHistory = React.useCallback(() => {
    const root = getProjectRoot(selectedVideo);
    if (!root) return;
    setHistoryLoading(true);
    axios.get(`${BACKEND_URL}/edit-history`, { params: { project_root: root, limit: 50 } })
      .then(r => setHistoryItems(r.data || []))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [selectedVideo, BACKEND_URL, getProjectRoot]);

  React.useEffect(() => {
    if (historyOpen) loadHistory();
  }, [historyOpen, loadHistory]);

  // Load cleanup data when switching to cleanup tab
  const loadCleanup = React.useCallback(() => {
    const root = getProjectRoot(selectedVideo);
    if (!root) return;
    setCleanupLoading(true);
    axios.get(`${BACKEND_URL}/translate/stale/${encodeURIComponent(root)}`)
      .then(r => setCleanupData(r.data))
      .catch(() => setCleanupData(null))
      .finally(() => setCleanupLoading(false));
  }, [selectedVideo, BACKEND_URL, getProjectRoot]);

  React.useEffect(() => {
    if (panelView === 'cleanup') loadCleanup();
  }, [panelView, loadCleanup]);

  // Editors list for assignment dropdown
  const [editors, setEditors] = React.useState([]);
  React.useEffect(() => {
    axios.get(`${BACKEND_URL}/users/editors`).then(r => setEditors(r.data || [])).catch(() => {});
  }, [BACKEND_URL]);

  const revertEdit = React.useCallback(async (editId) => {
    if (!await confirm({
      message: 'Revert this edit? The previous value will be restored.',
      title: 'Revert edit?',
      confirmLabel: 'Revert',
      destructive: true,
    })) return;
    axios.post(`${BACKEND_URL}/edit-history/${editId}/revert`)
      .then(() => { loadHistory(); onRefresh(); })
      .catch(err => pushToast('edit-revert-error', 'error', `Revert failed: ${err?.response?.data?.detail || err.message}`));
  }, [BACKEND_URL, loadHistory, onRefresh, pushToast, confirm]);
  const unifiedScrollRef = React.useRef(null);
  const blockRefsMap = React.useRef({});

  // Search & Replace state
  const [showReplace, setShowReplace]   = React.useState(false);
  const [replaceTerm, setReplaceTerm]   = React.useState('');
  const [replaceScope, setReplaceScope] = React.useState('video');
  const [caseFlag, setCaseFlag]         = React.useState(false);
  const [previewRows, setPreviewRows]   = React.useState(null);   // null | []
  const [replaceMsg, setReplaceMsg]     = React.useState('');
  const [replacing, setReplacing]       = React.useState(false);

  // Part-scoped pool — used for the admin video-ID mismatch notice
  const colMarks   = marks.filter(m => getProjectRoot(m.video_id) === getProjectRoot(selectedVideo) && m.part_number === selectedPart);

  const resetReplace = () => { setPreviewRows(null); setReplaceMsg(''); };

  const runPreview = async () => {
    if (!searchTerm.trim()) return;
    setReplacing(true); setReplaceMsg(''); setPreviewRows(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/marks/search-replace`, {
        find: searchTerm, replace: replaceTerm,
        scope: replaceScope, video_id: selectedVideo, part: selectedPart,
        preview: true, case_sensitive: caseFlag,
      });
      setPreviewRows(res.data.rows || []);
      setReplaceMsg(`${res.data.match_count} match${res.data.match_count !== 1 ? 'es' : ''} found`);
    } catch (e) {
      setReplaceMsg(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setReplacing(false); }
  };

  const runCommit = async () => {
    if (!previewRows || previewRows.length === 0) return;
    setReplacing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/marks/search-replace`, {
        find: searchTerm, replace: replaceTerm,
        scope: replaceScope, video_id: selectedVideo, part: selectedPart,
        preview: false, case_sensitive: caseFlag,
      });
      setReplaceMsg(`✓ Replaced in ${res.data.updated_count} row${res.data.updated_count !== 1 ? 's' : ''}`);
      setPreviewRows(null);
      onRefresh();
    } catch (e) {
      setReplaceMsg(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setReplacing(false); }
  };

  const languageOptions = React.useMemo(() => {
    const byKey = new Map();
    const addLanguage = (language) => {
      const key = normalizeLanguageKey(language);
      if (!key || byKey.has(key)) return;
      const meta = getLanguageMeta(language);
      byKey.set(key, { key, label: meta.label, meta });
    };

    if (sourceLanguage) addLanguage(sourceLanguage);
    if (preferredEditingLanguage) addLanguage(preferredEditingLanguage);
    marks.forEach((mark) => {
      Object.entries(mark.translations || {}).forEach(([language, text]) => {
        if (String(text || '').trim()) addLanguage(language);
      });
    });
    // Also include languages from unified blocks (VTT files)
    unifiedBlocks.forEach((block) => {
      Object.entries(block.translations || {}).forEach(([language, text]) => {
        if (String(text || '').trim()) addLanguage(language);
      });
    });

    return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [marks, unifiedBlocks, sourceLanguage, preferredEditingLanguage]);

  const preferredLanguageKey = React.useMemo(
    () => normalizeLanguageKey(preferredEditingLanguage || sourceLanguage),
    [preferredEditingLanguage, sourceLanguage]
  );

  React.useEffect(() => {
    setEditingLanguage(prev => {
      // When the preferred language changes (interview language set on subject), follow it
      if (preferredLanguageKey && languageOptions.some(option => option.key === preferredLanguageKey)) return preferredLanguageKey;
      if (prev && languageOptions.some(option => option.key === prev)) return prev;
      return languageOptions[0]?.key || '';
    });
  }, [languageOptions, preferredLanguageKey, selectedVideo]);

  React.useEffect(() => {
    setVisibleLanguages(prev => {
      const validKeys = new Set(languageOptions.map(option => option.key));
      let next = prev.filter(key => validKeys.has(key));
      if (editingLanguage && !next.includes(editingLanguage)) next = [editingLanguage, ...next];
      if (!next.length && editingLanguage) next = [editingLanguage];
      next = [...new Set(next)];
      if (next.length === prev.length && next.every((key, idx) => key === prev[idx])) {
        return prev;
      }
      return next;
    });
  }, [editingLanguage, languageOptions]);

  const showAllLanguages = React.useCallback(() => {
    setVisibleLanguages(languageOptions.map(option => option.key));
  }, [languageOptions]);

  const showOnlyEditingLanguage = React.useCallback(() => {
    setVisibleLanguages(editingLanguage ? [editingLanguage] : []);
  }, [editingLanguage]);

  const toggleVisibleLanguage = React.useCallback((languageKey) => {
    setVisibleLanguages(prev => {
      if (languageKey === editingLanguage) return prev.includes(languageKey) ? prev : [languageKey, ...prev];
      return prev.includes(languageKey)
        ? prev.filter(key => key !== languageKey)
        : [...prev, languageKey];
    });
  }, [editingLanguage]);

  // ── Unified view: fetch all VTT blocks when mode is active ──
  // Extracted from the effect so the failure state can offer a working Retry.
  const loadUnified = React.useCallback(() => {
    if (!unifiedMode || !selectedVideo) {
      setUnifiedBlocks([]);
      setUnifiedError(false);
      return;
    }
    setUnifiedLoading(true);
    axios.get(`${BACKEND_URL}/transcript-unified/${encodeURIComponent(selectedVideo)}`, {
      params: { part: selectedPart },
    })
      .then(res => {
        setUnifiedBlocks(res.data || []);
        setUnifiedError(false);
        // Auto-show all languages found in unified blocks
        const langs = new Set();
        (res.data || []).forEach(b => {
          Object.keys(b.translations || {}).forEach(k => langs.add(normalizeLanguageKey(k)));
        });
        if (langs.size > 0) setVisibleLanguages(Array.from(langs));
      })
      // A failed fetch used to empty the blocks, which rendered as "No subtitle
      // blocks found for Tape N" — telling the editor their transcript did not
      // exist when the request had simply failed. Keep whatever is on screen
      // and say what actually happened.
      .catch(() => setUnifiedError(true))
      .finally(() => setUnifiedLoading(false));
  }, [unifiedMode, selectedVideo, selectedPart, BACKEND_URL]);

  React.useEffect(() => { loadUnified(); }, [loadUnified]);

  // ── Fetch the interview-level speaker map (empty unless diarization has run) ──
  React.useEffect(() => {
    const root = selectedVideo ? getProjectRoot(selectedVideo) : '';
    if (!root) { setSpeakerRows([]); return; }
    axios.get(`${BACKEND_URL}/speakers/${encodeURIComponent(root)}`)
      .then(res => {
        setSpeakerRows(res.data || []);
      })
      .catch(() => setSpeakerRows([]));
  }, [selectedVideo, BACKEND_URL, getProjectRoot]);

  const renameSpeaker = React.useCallback((clusterId, label) => {
    const root = selectedVideo ? getProjectRoot(selectedVideo) : '';
    const trimmed = (label || '').trim();
    if (!root || !trimmed) return;
    axios.put(`${BACKEND_URL}/speakers/${encodeURIComponent(root)}/${encodeURIComponent(clusterId)}`, { label: trimmed })
      .then(() => setSpeakerRows(rows => rows.map(r => r.cluster_id === clusterId ? { ...r, label: trimmed } : r)))
      .catch(() => {});
  }, [selectedVideo, BACKEND_URL, getProjectRoot]);

  const reassignCueSpeaker = React.useCallback((block, nextCluster) => {
    if (!block || !nextCluster) return;
    const ts = block.timestamp_start;
    const part = block.part_number ?? selectedPart;
    // optimistic: flip this cue's pill immediately
    setUnifiedBlocks(bs => bs.map(b =>
      (b.timestamp_start === ts && (b.part_number ?? selectedPart) === part) ? { ...b, speaker: nextCluster } : b));
    axios.put(`${BACKEND_URL}/cue-speaker`, { video_id: block.video_id, part, timestamp_start: ts, speaker: nextCluster })
      .catch(() => {
        // on failure, re-fetch to restore the true state
        axios.get(`${BACKEND_URL}/transcript-unified/${encodeURIComponent(selectedVideo)}`, { params: { part: selectedPart } })
          .then(res => setUnifiedBlocks(res.data || [])).catch(() => {});
      });
  }, [BACKEND_URL, selectedVideo, selectedPart]);

  // ── Fetch word-level split data (only straddling cues; empty until alignment) ──
  // Re-key by cue start at 2dp to match the cue keying MarkCard/blocks use.
  const rekeyCueWords = React.useCallback((cues) => {
    const out = {};
    Object.values(cues || {}).forEach(c => {
      if (c && c.cue_start != null) out[c.cue_start.toFixed(2)] = c;
    });
    return out;
  }, []);
  React.useEffect(() => {
    if (!selectedVideo) { setCueWords({}); return; }
    axios.get(`${BACKEND_URL}/cue-words/${encodeURIComponent(selectedVideo)}`, { params: { part: selectedPart } })
      .then(res => setCueWords(rekeyCueWords(res.data && res.data.cues)))
      .catch(() => setCueWords({}));
  }, [selectedVideo, selectedPart, BACKEND_URL, rekeyCueWords]);

  const reassignWord = React.useCallback((block, wordIndex, nextCluster) => {
    if (!block || wordIndex == null || !nextCluster) return;
    const ts = block.timestamp_start;
    const part = block.part_number ?? selectedPart;
    const key = (ts ?? 0).toFixed(2);
    // optimistic: recolour this word immediately
    setCueWords(cw => {
      const cue = cw[key];
      if (!cue) return cw;
      const words = cue.words.map(w => w.index === wordIndex ? { ...w, speaker: nextCluster } : w);
      const straddles = new Set(words.map(w => w.speaker)).size > 1;
      return { ...cw, [key]: { ...cue, words, straddles } };
    });
    axios.put(`${BACKEND_URL}/cue-words/reassign`, {
      video_id: block.video_id, part, cue_start: ts, word_index: wordIndex, speaker: nextCluster,
    })
      .then(res => {
        if (res.data && Array.isArray(res.data.words)) {
          setCueWords(cw => ({ ...cw, [key]: { cue_start: res.data.cue_start, straddles: res.data.straddles, words: res.data.words } }));
        }
      })
      .catch(() => {
        // on failure, re-fetch the true state
        axios.get(`${BACKEND_URL}/cue-words/${encodeURIComponent(selectedVideo)}`, { params: { part: selectedPart } })
          .then(res => setCueWords(rekeyCueWords(res.data && res.data.cues))).catch(() => {});
      });
  }, [BACKEND_URL, selectedVideo, selectedPart, rekeyCueWords]);

  const swapSpeakers = React.useCallback(() => {
    const root = selectedVideo ? getProjectRoot(selectedVideo) : '';
    if (!root) return;
    axios.post(`${BACKEND_URL}/speakers/${encodeURIComponent(root)}/swap`, {})
      .then(() => axios.get(`${BACKEND_URL}/speakers/${encodeURIComponent(root)}`))
      .then(res => {
        setSpeakerRows(res.data || []);
      })
      .catch(() => {});
  }, [BACKEND_URL, selectedVideo, getProjectRoot]);

  const downloadSpeakers = React.useCallback(() => {
    if (!selectedVideo) return;
    axios.get(`${BACKEND_URL}/download/${encodeURIComponent(selectedVideo)}`, { params: { part: selectedPart, speakers: 1 }, responseType: 'blob' })
      .then(res => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedVideo}_PT${selectedPart}_speakers.vtt`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => {});
  }, [BACKEND_URL, selectedVideo, selectedPart]);

  // ── Cross-part search: when searching in unified mode, query ALL parts ──
  React.useEffect(() => {
    if (!unifiedMode || !selectedVideo || !searchTerm.trim()) {
      setSearchResults(null);
      return;
    }
    const q = searchTerm.trim();
    if (q.length < 2) { setSearchResults(null); return; }
    const t = setTimeout(() => {
      axios.get(`${BACKEND_URL}/transcript-search/${encodeURIComponent(selectedVideo)}`, { params: { q } })
        .then(res => setSearchResults(res.data || []))
        .catch(() => setSearchResults(null));
    }, 400);
    return () => clearTimeout(t);
  }, [unifiedMode, selectedVideo, searchTerm, BACKEND_URL]);

  // ── Unified auto-scroll: track video playback and highlight active block ──
  React.useEffect(() => {
    if (!unifiedMode || !videoRef?.current || unifiedBlocks.length === 0) return;
    const video = videoRef.current;
    const onTimeUpdate = () => {
      const t = video.currentTime;
      let idx = -1;
      for (let i = 0; i < unifiedBlocks.length; i++) {
        if (t >= unifiedBlocks[i].timestamp_start && t < unifiedBlocks[i].timestamp_end) {
          idx = i;
          break;
        }
      }
      // If between blocks, find the closest preceding block
      if (idx === -1) {
        for (let i = unifiedBlocks.length - 1; i >= 0; i--) {
          if (t >= unifiedBlocks[i].timestamp_start) { idx = i; break; }
        }
      }
      const key = idx >= 0 ? blockKey(unifiedBlocks[idx]) : null;
      setActiveBlockKey(prev => prev === key ? prev : key);
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [unifiedMode, unifiedBlocks, videoRef, blockKey]);

  // Scroll the active block to the TOP of the list so it sits in a fixed place.
  //
  // Resolved by cue identity, not by position. It used to index blockRefsMap
  // with a position computed against the UNFILTERED block list while the refs
  // were keyed by position in the FILTERED render. With any filter on, those
  // two spaces diverge: playing at 1:36 would scroll to whatever cue happened
  // to sit at that position in the filtered list — a cue twenty minutes away —
  // and clicking a timecode looked like it had seeked to the wrong place, when
  // in fact the seek was right and the list had jumped.
  //
  // A cue that is filtered out simply has no ref, so the list holds still
  // instead of scrolling somewhere arbitrary.
  React.useEffect(() => {
    if (!activeBlockKey || !unifiedMode) return;
    const el = blockRefsMap.current[activeBlockKey];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeBlockKey, unifiedMode]);

  // Resizable transcript column — this is the most-used panel in the app and was
  // the one split layout that could not be resized (fixed 40%). A pointer-drag
  // handle on its left edge (pointer events, so it works on iPad too) sets a
  // width persisted in sessionStorage. The column is anchored to the right edge,
  // so width = viewport width minus the pointer's X. Clamped to the old bounds.
  const R_MIN = 460, R_MAX = 760;
  const [panelWidth, setPanelWidth] = React.useState(() => {
    const saved = parseInt(sessionStorage.getItem('gwv_transcript_w'), 10);
    return Number.isFinite(saved) ? Math.min(R_MAX, Math.max(R_MIN, saved)) : null;
  });
  const resizingRef = React.useRef(false);
  const startResize = React.useCallback((e) => {
    e.preventDefault();
    resizingRef.current = true;
    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const w = Math.min(R_MAX, Math.max(R_MIN, window.innerWidth - ev.clientX));
      setPanelWidth(w);
    };
    const onUp = () => {
      resizingRef.current = false;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setPanelWidth((w) => { if (w != null) sessionStorage.setItem('gwv_transcript_w', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, []);

  return (
    <div style={{ position: 'relative', flex: panelWidth ? `0 0 ${panelWidth}px` : '0 0 40%', width: panelWidth ? `${panelWidth}px` : '40%', minWidth: `${R_MIN}px`, maxWidth: `${R_MAX}px`, background: theme.sidebar, borderLeft: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
      {/* Drag handle straddling the left border; highlights on hover. */}
      <div
        onPointerDown={startResize}
        title="Drag to resize the transcript column"
        style={{ position: 'absolute', left: '-3px', top: 0, bottom: 0, width: '7px', cursor: 'col-resize', zIndex: 6, touchAction: 'none' }}
        onMouseEnter={e => { e.currentTarget.style.background = `${theme.highlight}55`; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      />
      <div style={{ padding: '18px 14px 10px', flexShrink: 0 }}>

        {/* Header badges + Unified / History / Cleanup toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0 0 14px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: theme.card, borderRadius: '8px', padding: '2px', border: `1px solid ${theme.border}`, gap: '2px' }}>
            {[
              { key: 'unified', label: t('editor.unified').toUpperCase(), icon: null, color: theme.glossaryAccent },
              { key: 'history', label: t('editor.history').toUpperCase(), icon: History, color: '#7c3aed' },
              { key: 'cleanup', label: t('editor.langFiles').toUpperCase(), icon: Sparkles, color: '#f59e0b' },
              { key: 'glossary', label: t('editor.glossary').toUpperCase(), icon: BookOpen, color: '#10b981' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setPanelView(tab.key)}
                style={{ padding: '4px 12px', fontWeight: 'bold', fontSize: '0.72rem', borderRadius: '6px', border: 'none', cursor: 'pointer', letterSpacing: '0.04em', whiteSpace: 'nowrap',
                  background: panelView === tab.key ? tab.color : 'transparent',
                  color: panelView === tab.key ? '#fff' : theme.textSub,
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                {tab.icon && <Icon icon={tab.icon} size={12} />}{tab.label}
              </button>
            ))}
          </div>
        </div>

        {languageOptions.length > 0 && (
          <div style={{ marginBottom: '12px', padding: '10px 12px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <LanguageSelector
                languages={languageOptions.map(o => o.key)}
                value={editingLanguage}
                onChange={val => { if (val && val !== 'off') setEditingLanguage(val); }}
                theme={theme}
                compact
                label="Interview Language"
              />
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
              <Button theme={theme} variant="secondary" size="sm" active={visibleLanguages.length <= 1} onClick={showOnlyEditingLanguage}>
                Interview only
              </Button>
              <Button theme={theme} variant="secondary" size="sm" active={visibleLanguages.length === languageOptions.length} onClick={showAllLanguages}>
                Show all
              </Button>
              {languageOptions.map(option => {
                const checked = visibleLanguages.includes(option.key);
                const locked = option.key === editingLanguage;
                return (
                  <label
                    key={option.key}
                    title={option.label}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '4px 8px',
                      borderRadius: '999px',
                      background: checked ? option.meta.bg : theme.sidebar,
                      border: `1px solid ${checked ? option.meta.border : theme.border}`,
                      color: checked ? option.meta.text : theme.text,
                      cursor: locked ? 'default' : 'pointer',
                      fontSize: theme.fsBadge,
                      fontWeight: 700,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={locked}
                      onChange={() => toggleVisibleLanguage(option.key)}
                      style={{ margin: 0 }}
                    />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      {getLanguageCode(option.key)}
                      {locked && <PenLine size={10} strokeWidth={2} />}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* Video-ID mismatch notice — admin only */}
        {userRole === 'admin' && selectedVideo && colMarks.length === 0 && marks.length > 0 && (
          <div style={{ fontSize: theme.fsBadge, color: theme.mutedText || '#888', background: `${theme.card || '#f5f5f5'}`, padding: '8px 12px', borderRadius: '6px', marginBottom: '10px', fontFamily: 'monospace', border: `1px solid ${theme.border || '#ddd'}` }}>
            <span style={{ display: 'inline-block', background: '#6366f1', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', marginRight: '6px', verticalAlign: 'middle', letterSpacing: '0.5px', fontFamily: 'system-ui, sans-serif' }}>ADMIN FYI</span>
            {marks.length} marks in DB but none match current video.<br/>
            DB video_id sample: <b>{marks[0]?.video_id}</b><br/>
            Current video_id: <b>{selectedVideo}</b><br/>
            Run <b>/admin/migrate-video-ids/commit</b> to fix.
          </div>
        )}

        {/* ── SEARCH BAR (marks mode only — hidden on Files/History) ── */}
        {panelView !== 'cleanup' && panelView !== 'glossary' && !historyOpen && <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Input
                theme={theme}
                placeholder="Search marks…"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); resetReplace(); }}
                style={{ paddingRight: '30px' }}
              />
              {searchTerm && (
                <button type="button" aria-label="Clear search"
                  onClick={() => { setSearchTerm(''); resetReplace(); }}
                  style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', background: 'none', border: 'none', color: 'inherit' }}>
                  <Icon icon={X} size={14} />
                </button>
              )}
            </div>
            <Button
              theme={theme}
              variant="secondary"
              size="sm"
              active={caseFlag}
              onClick={() => setCaseFlag(f => !f)}
              title="Case sensitive"
              style={{ fontFamily: 'monospace' }}
            >Aa</Button>
            {canEdit && (
              <Button
                theme={theme}
                variant="secondary"
                size="sm"
                active={showReplace}
                onClick={() => { setShowReplace(f => !f); resetReplace(); }}
                title="Search & replace"
                aria-expanded={showReplace}
              >Replace</Button>
            )}
          </div>

          {/* ── REPLACE PANEL ── */}
          {showReplace && (
            <div style={{ marginTop: '8px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Input
                theme={theme}
                placeholder="Replace with…"
                value={replaceTerm}
                onChange={e => { setReplaceTerm(e.target.value); resetReplace(); }}
              />
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 'bold', whiteSpace: 'nowrap' }}>SCOPE</span>
                {[
                  ['part',  `This tape (Tape ${selectedPart})`],
                  ['video', 'Whole project (all parts)'],
                ].map(([val, label]) => (
                  <Button key={val} theme={theme} variant="secondary" size="sm" active={replaceScope === val}
                    onClick={() => { setReplaceScope(val); resetReplace(); }}
                    style={{ flex: 1 }}>
                    {label}
                  </Button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Button
                  theme={theme}
                  variant="primary"
                  onClick={runPreview}
                  disabled={!searchTerm.trim()}
                  loading={replacing}
                  icon={Search}
                  style={{ flex: 1 }}
                >
                  {replacing ? 'Searching…' : 'Preview'}
                </Button>
                {previewRows && previewRows.length > 0 && (
                  <Button
                    theme={theme}
                    variant="accent"
                    onClick={runCommit}
                    disabled={replacing}
                    icon={Check}
                    style={{ flex: 1 }}
                  >
                    Replace {previewRows.length} row{previewRows.length !== 1 ? 's' : ''}
                  </Button>
                )}
              </div>
              {replaceMsg && (
                <div style={{ fontSize: theme.fsButton, color: replaceMsg.startsWith('✓') ? theme.success : theme.accent, fontFamily: 'monospace' }}>
                  {replaceMsg}
                </div>
              )}
              {previewRows && previewRows.length > 0 && (
                <div style={{ maxHeight: '220px', overflowY: 'auto', border: `1px solid ${theme.border}`, borderRadius: '6px' }}>
                  {previewRows.map(r => (
                    <div key={r.id} style={{ padding: '8px 10px', borderBottom: `1px solid ${theme.border}`, fontSize: theme.fsOriginalText }}>
                      <div style={{ color: theme.textSub, fontFamily: 'monospace', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        {cleanLabel(getProjectRoot(r.video_id))} · <Icon icon={Film} size={9} color={theme.textSub} /> Tape {r.part_number} · {formatVTTTime(r.timestamp_start)}
                      </div>
                      <div style={{ color: theme.accent, textDecoration: 'line-through', marginBottom: '2px', wordBreak: 'break-word' }}>
                        {r.working_text}
                      </div>
                      <div style={{ color: theme.success, wordBreak: 'break-word' }}>
                        {r.preview_text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {previewRows && previewRows.length === 0 && (
                <div style={{ fontSize: theme.fsOriginalText, color: theme.textSub, textAlign: 'center', padding: '6px 0' }}>
                  No matches found.
                </div>
              )}
            </div>
          )}
        </div>}

        {/* Filter tabs — marks mode or history mode */}
        {/* marks filter tabs removed — unified is the only view */}

        {/* Unified mode: filter + block count */}
        {unifiedMode && !unifiedLoading && unifiedBlocks.length > 0 && panelView !== 'cleanup' && panelView !== 'glossary' && !historyOpen && (() => {
          const isEdited = b => b.has_mark || (b.edited_text && b.edited_text.trim() !== b.original_text?.trim());
          const editedCount = unifiedBlocks.filter(isEdited).length;
          const pendingCount = unifiedBlocks.filter(b => b.has_mark && b.status === 'pending').length;
          const flaggedCount = unifiedBlocks.filter(b => b.flagged).length;
          const qaCount = unifiedBlocks.filter(b => b.qa_suggestion).length;
          const reviewedCount = unifiedBlocks.filter(b => b.status === 'completed').length;
          const filters = [
            ...(currentUserId ? [{ id: 'mine', label: 'MY TASKS', count: unifiedBlocks.filter(b => b.assigned_to === currentUserId || (hasRangeAssignments && isInAssignedRange(b.timestamp_start ?? b.start ?? 0))).length, color: '#0ea5e9' }] : []),
            { id: 'all', label: t('filter.all').toUpperCase(), count: unifiedBlocks.length, color: theme.glossaryAccent },
            { id: 'edited', label: t('filter.edited').toUpperCase(), count: editedCount, color: '#007aff' },
            { id: 'pending', label: t('filter.pending').toUpperCase(), count: pendingCount, color: theme.accent || '#e50914' },
            { id: 'flagged', label: t('filter.flagged').toUpperCase(), count: flaggedCount, color: '#f59e0b' },
            { id: 'qa', label: t('filter.qa').toUpperCase(), count: qaCount, color: '#f472b6' },
            { id: 'assigned', label: t('filter.assigned').toUpperCase(), count: unifiedBlocks.filter(b => b.assigned_to).length, color: '#7c3aed' },
            { id: 'reviewed', label: t('filter.reviewed').toUpperCase(), count: reviewedCount, color: '#34c759' },
          ];
          // Compare overlay summary. Only rendered when a second engine actually
          // ran on this part — most interviews have no compare data and should
          // not carry a bar telling them so.
          const comparable = unifiedBlocks.filter(b => b.speaker && b.speaker_alt);
          const disagreeing = comparable.filter(b => b.speaker !== b.speaker_alt);
          const agreementPct = comparable.length
            ? Math.round(1000 * (comparable.length - disagreeing.length) / comparable.length) / 10
            : null;
          return (
            <>
            {comparable.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                marginBottom: '8px', padding: '5px 10px', borderRadius: '8px',
                background: disagreeing.length ? '#f59e0b14' : theme.card,
                border: `1px solid ${disagreeing.length ? '#f59e0b44' : theme.border}`,
                fontSize: theme.fsBadge, flexWrap: 'wrap',
              }}>
                <span style={{ color: theme.textSub, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700 }}>
                  Two engines
                </span>
                <span style={{ color: theme.text, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {agreementPct}% agree
                </span>
                <span style={{ color: disagreeing.length ? '#f59e0b' : theme.textSub, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {disagreeing.length} to check
                </span>
                {disagreeing.length > 0 && (
                  <button
                    onClick={() => setUnifiedFilter(unifiedFilter === 'disagree' ? 'all' : 'disagree')}
                    title="Show only the cues where the two engines disagree"
                    style={{
                      padding: '3px 9px', minHeight: '26px', borderRadius: '6px', cursor: 'pointer',
                      border: `1px solid ${unifiedFilter === 'disagree' ? '#f59e0b' : theme.border}`,
                      background: unifiedFilter === 'disagree' ? '#f59e0b' : 'transparent',
                      color: unifiedFilter === 'disagree' ? '#1a1a1a' : theme.text,
                      fontWeight: 700, fontSize: theme.fsBadge, fontFamily: 'inherit',
                    }}>
                    {unifiedFilter === 'disagree' ? 'Showing only these' : 'Review them'}
                  </button>
                )}
                <button
                  onClick={() => setSpeakerCompareMode(v => !v)}
                  title={speakerCompareMode
                    ? 'Showing the compare engine\'s speakers. It is less accurate — switch back before trusting a label.'
                    : 'Show what the compare engine thinks instead'}
                  style={{
                    padding: '3px 9px', minHeight: '26px', borderRadius: '6px', cursor: 'pointer',
                    border: `1px solid ${speakerCompareMode ? '#7c3aed' : theme.border}`,
                    background: speakerCompareMode ? '#7c3aed' : 'transparent',
                    color: speakerCompareMode ? '#fff' : theme.textSub,
                    fontWeight: 700, fontSize: theme.fsBadge, fontFamily: 'inherit',
                  }}>
                  {speakerCompareMode ? 'Compare view' : 'Primary view'}
                </button>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', background: theme.card, borderRadius: '8px', padding: '2px', border: `1px solid ${theme.border}`, gap: '2px' }}>
                {filters.map(f => (
                  <button key={f.id} onClick={() => setUnifiedFilter(f.id)}
                    style={{ padding: '4px 8px', fontSize: '0.65rem', fontWeight: 'bold', letterSpacing: '0.03em', border: 'none', borderRadius: '6px', cursor: 'pointer',
                      background: unifiedFilter === f.id ? f.color : 'transparent',
                      color: unifiedFilter === f.id ? contrastText(f.color) : (f.count > 0 ? f.color : theme.textSub) }}>
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
              {unifiedFilter === 'pending' && pendingCount > 0 && canEdit && onCompleteAllPart && (
                <Button
                  theme={theme}
                  variant="success"
                  size="sm"
                  icon={Check}
                  onClick={onCompleteAllPart}
                  title={`Approve all ${pendingCount} pending corrections on this tape`}
                >
                  APPROVE TAPE ({pendingCount})
                </Button>
              )}
              {unifiedFilter === 'pending' && canEdit && (
                <button
                  onClick={async () => {
                    const root = getProjectRoot(selectedVideo);
                    if (!root) return;
                    if (!await confirm({
                      message:
                        `Approve ALL pending corrections across EVERY tape in this project?\n\n` +
                        `This will mark every pending correction in "${root}" as Reviewed by you, ` +
                        `including tapes you're not currently viewing.\n\nThis cannot be undone.`,
                      title: 'Approve entire project?',
                      confirmLabel: 'Approve',
                      destructive: false,
                    })) return;
                    axios.put(`${BACKEND_URL}/marks/bulk-complete-project/${encodeURIComponent(root)}`)
                      .then(r => {
                        const n = r.data?.completed_count || 0;
                        pushToast('approve-project', n ? 'success' : 'error',
                          n ? `Approved ${n} pending correction(s) across the project` : 'Nothing pending to approve');
                        if (onRefresh) onRefresh();
                        setTimeout(() => {
                          axios.get(`${BACKEND_URL}/transcript-unified/${encodeURIComponent(selectedVideo)}`, {
                            params: { part: selectedPart },
                          }).then(res => setUnifiedBlocks(res.data || [])).catch(() => {});
                        }, 300);
                      })
                      .catch(err => pushToast('approve-project', 'error', `Failed: ${err?.response?.data?.detail || err.message}`));
                  }}
                  title="Approve every pending correction across all tapes in this project"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '5px 10px', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.03em',
                    background: 'transparent', color: '#34c759', border: '1px solid #34c75988', borderRadius: '6px', cursor: 'pointer',
                  }}
                >
                  <Icon icon={Check} size={11} />APPROVE PROJECT
                </button>
              )}
            </div>
            </>
          );
        })()}

      </div>

      <div ref={unifiedScrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 14px 20px' }}>
        {/* Range assignment info bar */}
        {panelView === 'unified' && hasRangeAssignments && currentPartRanges && currentPartRanges.length > 0 && (
          <div style={{ margin: '0 0 8px', padding: '6px 10px', borderRadius: '8px', background: `${theme.accent || '#3b82f6'}12`, border: `1px solid ${theme.accent || '#3b82f6'}30`, fontSize: '0.68rem', color: theme.accent || '#3b82f6', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <Lock size={10} style={{ flexShrink: 0 }} />
            <span style={{ fontWeight: 600 }}>Your assigned range{currentPartRanges.length > 1 ? 's' : ''}:</span>
            {currentPartRanges.map((a, i) => {
              const fmtTC = (s) => { if (s === null || s === undefined) return ''; const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`; };
              const range = a.tc_start !== null || a.tc_end !== null
                ? `${fmtTC(a.tc_start || 0)} – ${a.tc_end !== null ? fmtTC(a.tc_end) : 'end'}`
                : 'Full tape';
              return (
                <span key={i} style={{ background: `${theme.accent || '#3b82f6'}20`, padding: '1px 6px', borderRadius: '4px', fontWeight: 500 }}>
                  {a.part_number !== null ? `Tape ${a.part_number}: ` : ''}{range}
                  {a.note && <span style={{ opacity: 0.7, marginLeft: '4px' }}>({a.note})</span>}
                </span>
              );
            })}
          </div>
        )}
        {panelView === 'cleanup' ? (
          /* ── Cleanup view: per-tape language matrix ── */
          cleanupLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSub }}>
              <Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : !cleanupData ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSub, fontSize: '0.82rem' }}>
              Unable to load cleanup data.
            </div>
          ) : (() => {
            const { tapes = [], all_languages = [], interview_language, stale_db_languages = [], stale_db_mark_count = 0 } = cleanupData;
            const langNames = {};
            all_languages.forEach(l => { langNames[l] = (getLanguageMeta(l)?.label || l); });
            // Sort: interview language first, then English, then divider, then others alphabetically
            const primaryLangs = [];
            const otherLangs = [];
            if (interview_language && all_languages.includes(interview_language)) primaryLangs.push(interview_language);
            if (interview_language !== 'en' && all_languages.includes('en')) primaryLangs.push('en');
            all_languages.forEach(l => {
              if (!primaryLangs.includes(l)) otherLangs.push(l);
            });
            otherLangs.sort((a, b) => (langNames[a] || a).localeCompare(langNames[b] || b));
            const sortedLangs = [...primaryLangs, ...otherLangs];
            const dividerAfterIndex = primaryLangs.length - 1; // index in sortedLangs after which to show divider
            const cellStyle = { padding: '6px 4px', textAlign: 'center', fontSize: '0.68rem', borderBottom: `1px solid ${theme.border}` };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* ── Translation Pipeline (above grid) ── */}
                {canEdit && tapes.length > 0 && (() => {
                  const sourceLang = interview_language
                    || (all_languages.includes('en') ? 'en' : (all_languages[0] || 'en'));
                  const sourceLangMissing = !interview_language;
                  return (
                  <div style={{ background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, padding: '14px' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.text, letterSpacing: '0.04em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon icon={Globe} size={14} color="#3b82f6" />
                      TRANSLATION PIPELINE
                      {sourceLangMissing && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.55rem', fontWeight: 600, color: '#f59e0b', background: '#f59e0b18', border: '1px solid #f59e0b44', padding: '2px 6px', borderRadius: '4px', letterSpacing: 0 }}>
                          interview language not set — using {(getLanguageMeta(sourceLang)?.label || sourceLang).toUpperCase()}
                          <select
                            value=""
                            onChange={(e) => {
                              const lang = e.target.value;
                              if (!lang) return;
                              axios.put(`${BACKEND_URL}/pipeline/language`, {
                                project_root: cleanupData.root,
                                language: lang,
                                confirmed: true,
                              })
                                .then(() => {
                                  pushToast('set-lang', 'success', `Interview language set to ${(getLanguageMeta(lang)?.label || lang).toUpperCase()}`);
                                  loadCleanup();
                                })
                                .catch(err => pushToast('set-lang', 'error', `Failed: ${err?.response?.data?.detail || err.message}`));
                            }}
                            style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b66', borderRadius: '3px', padding: '1px 4px', fontSize: '0.55rem', fontWeight: 700, cursor: 'pointer' }}
                          >
                            <option value="">Set…</option>
                            {(all_languages.length > 0 ? all_languages : ['en','sv','de','he','fr','es','pl','ru','yi']).map(l => (
                              <option key={l} value={l} style={{ background: theme.card, color: theme.text }}>
                                {(getLanguageMeta(l)?.label || l).toUpperCase()}
                              </option>
                            ))}
                          </select>
                        </span>
                      )}
                    </div>

                    {/* Step 1: Write Corrected Source VTTs */}
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: theme.textSub, marginBottom: '6px', letterSpacing: '0.03em' }}>
                        STEP 1 — Write corrected {(getLanguageMeta(sourceLang)?.label || sourceLang).toUpperCase()} VTTs from edits
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {tapes.map(tape => (
                          <button
                            key={`wc-${tape.tape_number}`}
                            disabled={cleanupRunning}
                            onClick={() => {
                              setCleanupRunning(true);
                              axios.post(`${BACKEND_URL}/translate/write-corrected/${encodeURIComponent(cleanupData.root)}?lang=${sourceLang}`)
                                .then(r => {
                                  const written = r.data?.files_written?.length || 0;
                                  pushToast(`wc-t${tape.tape_number}`, written ? 'success' : 'error', written ? `Wrote ${written} corrected file(s)` : 'No corrections found in database');
                                  loadCleanup();
                                })
                                .catch(err => pushToast(`wc-t${tape.tape_number}`, 'error', `Failed: ${err?.response?.data?.detail || err.message}`))
                                .finally(() => setCleanupRunning(false));
                            }}
                            style={{ padding: '3px 8px', fontSize: '0.62rem', fontWeight: 600, borderRadius: '5px', border: `1px solid ${theme.border}`, background: theme.sidebar, color: theme.text, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                            title={`Write corrected VTT for Tape ${tape.tape_number}`}
                          >
                            <Icon icon={FileEdit} size={9} />T{tape.tape_number}
                          </button>
                        ))}
                        <button
                          disabled={cleanupRunning}
                          onClick={() => {
                            setCleanupRunning(true);
                            axios.post(`${BACKEND_URL}/translate/write-corrected/${encodeURIComponent(cleanupData.root)}?lang=${sourceLang}`)
                              .then(r => {
                                const written = r.data?.files_written?.length || 0;
                                pushToast('wc-all', written ? 'success' : 'error', written ? `Wrote ${written} corrected file(s)` : 'No corrections found in database');
                                loadCleanup();
                              })
                              .catch(err => pushToast('wc-all', 'error', `Failed: ${err?.response?.data?.detail || err.message}`))
                              .finally(() => setCleanupRunning(false));
                          }}
                          style={{ padding: '3px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '5px', border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                        >
                          <Icon icon={FileEdit} size={9} />Write All
                        </button>
                      </div>
                    </div>

                    {/* Step 2: Translate to English */}
                    <div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: theme.textSub, marginBottom: '6px', letterSpacing: '0.03em' }}>
                        STEP 2 — Translate {(getLanguageMeta(sourceLang)?.label || sourceLang).toUpperCase()} → ENGLISH
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {tapes.map(tape => (
                          <button
                            key={`tr-${tape.tape_number}`}
                            disabled={cleanupRunning}
                            onClick={async () => {
                              if (!await confirm({
                                message: `Translate Tape ${tape.tape_number} from ${(getLanguageMeta(sourceLang)?.label || sourceLang)} to English?\n\nExisting English VTT will be versioned first.`,
                                title: 'Translate tape?',
                                confirmLabel: 'Translate',
                                destructive: false,
                              })) return;
                              setCleanupRunning(true);
                              const toastId = `tr-t${tape.tape_number}`;
                              pushToast(toastId, 'running', `Translating Tape ${tape.tape_number} → English…`);
                              axios.post(`${BACKEND_URL}/translate/${encodeURIComponent(cleanupData.root)}?target=en&source=${sourceLang}&part=${tape.tape_number}`)
                                .then(r => {
                                  pushToast(toastId, 'success', `Tape ${tape.tape_number}: translation started (${r.data?.total_cues || 0} cues)`);
                                  setTimeout(() => { loadCleanup(); setCleanupRunning(false); }, 3000);
                                })
                                .catch(err => { pushToast(toastId, 'error', `Tape ${tape.tape_number} failed: ${err?.response?.data?.detail || err.message}`); setCleanupRunning(false); });
                            }}
                            style={{ padding: '3px 8px', fontSize: '0.62rem', fontWeight: 600, borderRadius: '5px', border: `1px solid ${theme.border}`, background: theme.sidebar, color: theme.text, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                            title={`Translate Tape ${tape.tape_number} to English`}
                          >
                            <Icon icon={Globe} size={9} />T{tape.tape_number}
                          </button>
                        ))}
                        <button
                          disabled={cleanupRunning}
                          onClick={async () => {
                            if (!await confirm({
                              message: `Translate ALL tapes from ${(getLanguageMeta(sourceLang)?.label || sourceLang)} to English?\n\nExisting English VTTs will be versioned first.`,
                              title: 'Translate all tapes?',
                              confirmLabel: 'Translate All',
                              destructive: false,
                            })) return;
                            setCleanupRunning(true);
                            pushToast('tr-all', 'running', 'Translating all tapes → English…');
                            axios.post(`${BACKEND_URL}/translate/${encodeURIComponent(cleanupData.root)}?target=en&source=${sourceLang}`)
                              .then(r => {
                                pushToast('tr-all', 'success', `Translation started: ${r.data?.source_files || 0} file(s), ${r.data?.total_cues || 0} cues`);
                                setTimeout(() => { loadCleanup(); setCleanupRunning(false); }, 5000);
                              })
                              .catch(err => { pushToast('tr-all', 'error', `Failed: ${err?.response?.data?.detail || err.message}`); setCleanupRunning(false); });
                          }}
                          style={{ padding: '3px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '5px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                        >
                          <Icon icon={Globe} size={9} />Translate All → EN
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })()}

                {/* Step 3: VTT Actions */}
                <div style={{ background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: theme.text, letterSpacing: '0.04em' }}>STEP 3 — VTT ACTIONS</span>
                    {onDownloadAllParts && (
                      <button onClick={onDownloadAllParts}
                        style={{ padding: '4px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', border: `1px solid ${theme.glossaryAccent || '#6366f1'}`, background: 'transparent', color: theme.glossaryAccent || '#6366f1', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Icon icon={DownloadCloud} size={10} />Download All (ZIP)
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {onWriteCorrected && (
                      <Button theme={theme} variant="success" size="sm"
                        onClick={() => onWriteCorrected(interview_language)}
                        disabled={cleanupRunning}
                        title="Apply all corrections from the database to the VTT files. Only applies to files that have been edited. Originals backed up."
                        icon={FileEdit}
                        style={{ flex: 1 }}>
                        Write Corrected
                      </Button>
                    )}
                    {onRefreshAllVTT && (
                      <button onClick={onRefreshAllVTT}
                        disabled={cleanupRunning}
                        title="Rewrite ALL VTT files (all languages, all tapes) with corrections applied. Originals backed up."
                        style={{ flex: 1, padding: '6px 10px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', opacity: cleanupRunning ? 0.5 : 1 }}>
                        <Icon icon={RefreshCw} size={10} />Refresh All VTTs
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '0.52rem', color: theme.textSub, lineHeight: 1.4 }}>
                    <strong>Write Corrected</strong> — applies your edits to the source language VTTs only (edited blocks). <strong>Refresh All</strong> — rewrites every VTT file across all languages with corrections. Both back up originals to VTT_BACKUP/.
                  </div>
                </div>

                {/* Step 4: Translate to additional languages */}
                {onTranslateLanguage && canEdit && (
                  <div style={{ background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: theme.text, letterSpacing: '0.04em' }}>STEP 4 — TRANSLATE TO ADDITIONAL LANGUAGES</div>
                      {onRebuildAllTranslations && userRole === 'admin' && (() => {
                        const targets = subjectVttLangs.filter(l => l && l !== (interview_language || 'en'));
                        const hasTargets = targets.length > 0;
                        return (
                          <button
                            onClick={() => !rebuildingTranslations && hasTargets && onRebuildAllTranslations(targets)}
                            disabled={!hasTargets || rebuildingTranslations}
                            title={hasTargets
                              ? `Re-translate all ${targets.length} existing language${targets.length !== 1 ? 's' : ''} from the source VTTs. Old files are backed up to VTT_VERSIONS/.`
                              : 'No target languages to rebuild — add one first.'}
                            style={{
                              padding: '4px 10px', borderRadius: '8px', fontSize: '0.6rem', fontWeight: 700,
                              border: `1px solid ${hasTargets ? '#f59e0b' : theme.border}`,
                              background: hasTargets ? 'rgba(245,158,11,0.12)' : 'transparent',
                              color: hasTargets ? '#f59e0b' : theme.textSub,
                              cursor: hasTargets && !rebuildingTranslations ? 'pointer' : 'not-allowed',
                              opacity: rebuildingTranslations ? 0.5 : 1,
                              display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                            }}
                          >
                            <Icon icon={RefreshCw} size={10} style={{ animation: rebuildingTranslations ? 'spin 1s linear infinite' : 'none' }} />
                            {rebuildingTranslations ? 'Rebuilding…' : 'Rebuild All Translations'}
                          </button>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {[{c:'ar',l:'Arabic'},{c:'bg',l:'Bulgarian'},{c:'hr',l:'Croatian'},{c:'cs',l:'Czech'},{c:'da',l:'Danish'},{c:'nl',l:'Dutch'},{c:'en',l:'English'},{c:'et',l:'Estonian'},{c:'fi',l:'Finnish'},{c:'fr',l:'French'},{c:'de',l:'German'},{c:'el',l:'Greek'},{c:'he',l:'Hebrew'},{c:'hu',l:'Hungarian'},{c:'it',l:'Italian'},{c:'lv',l:'Latvian'},{c:'lt',l:'Lithuanian'},{c:'no',l:'Norwegian'},{c:'pl',l:'Polish'},{c:'pt',l:'Portuguese'},{c:'ro',l:'Romanian'},{c:'ru',l:'Russian'},{c:'sr',l:'Serbian'},{c:'sk',l:'Slovak'},{c:'sl',l:'Slovenian'},{c:'es',l:'Spanish'},{c:'sv',l:'Swedish'},{c:'tr',l:'Turkish'},{c:'uk',l:'Ukrainian'},{c:'yi',l:'Yiddish'}]
                        .map(({c, l: label}) => {
                          const exists = subjectVttLangs.includes(c);
                          const isTranslating = translatingLangs.has(c);
                          return (
                            <button key={c}
                              disabled={exists || isTranslating}
                              onClick={() => !exists && !isTranslating && onTranslateLanguage(c, label)}
                              title={exists ? `${label} — already translated` : isTranslating ? `Translating to ${label}…` : `Translate to ${label}`}
                              style={{
                                padding: '3px 8px', borderRadius: '10px', fontSize: '0.6rem', fontWeight: 700,
                                cursor: exists ? 'default' : 'pointer',
                                border: exists ? `1.5px solid ${theme.glossaryAccent}` : `1px dashed ${theme.border}`,
                                background: exists ? `${theme.glossaryAccent}18` : 'transparent',
                                color: exists ? theme.glossaryAccent : theme.textSub,
                                opacity: isTranslating ? 0.5 : 1,
                              }}>
                              {isTranslating ? '…' : ''}{label}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Per-tape language matrix table */}
                {tapes.length > 0 && sortedLangs.length > 0 ? (
                  <div style={{ background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, padding: '14px' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: theme.text, letterSpacing: '0.04em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon icon={Sparkles} size={14} color="#f59e0b" />
                      LANGUAGE INVENTORY
                      {interview_language && (
                        <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: 600, background: '#3b82f6', color: '#fff', marginLeft: '4px' }}>
                          Source: {(langNames[interview_language] || interview_language).toUpperCase()}
                        </span>
                      )}
                      <span style={{ flex: 1 }} />
                      <Button
                        theme={theme}
                        variant="outline"
                        size="sm"
                        icon={RotateCcw}
                        onClick={loadCleanup}
                        disabled={cleanupLoading}
                      >
                        Rescan
                      </Button>
                    </div>
                    <div style={{ overflowX: 'auto', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                      <thead>
                        <tr style={{ background: theme.card }}>
                          <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, color: theme.text, padding: '8px 10px', minWidth: '70px' }}>Tape</th>
                          {sortedLangs.map((l, li) => (
                            <React.Fragment key={l}>
                              {li === dividerAfterIndex + 1 && otherLangs.length > 0 && (
                                <th style={{ ...cellStyle, padding: '8px 0', width: '3px', minWidth: '3px' }}>
                                  <div style={{ width: '2px', height: '100%', minHeight: '14px', background: theme.border, margin: '0 auto', borderRadius: '1px' }} />
                                </th>
                              )}
                              <th style={{ ...cellStyle, fontWeight: 700, color: l === interview_language ? '#3b82f6' : l === 'en' ? '#10b981' : theme.text, padding: '8px 4px', minWidth: '50px', fontSize: '0.62rem', lineHeight: 1.3 }}>
                                <div>{langNames[l] || l}</div>
                                <div style={{ fontSize: '0.5rem', fontWeight: 600, opacity: 0.5, textTransform: 'uppercase' }}>{l}</div>
                              </th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tapes.map((tape, ti) => (
                          <tr key={ti} style={{ background: ti % 2 === 0 ? 'transparent' : theme.card }}>
                            <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, color: theme.text, padding: '6px 10px', whiteSpace: 'nowrap' }}>
                              {tape.tape_number > 0 ? `Tape ${tape.tape_number}` : tape.base}
                            </td>
                            {sortedLangs.map((l, li) => {
                              const langInfo = tape.languages[l];
                              const hasFile = !!langInfo;
                              const isSource = l === interview_language && tape.has_source;
                              const existingFormats = hasFile ? (langInfo.formats || []) : [];
                              const hasVtt = hasFile && langInfo.has_vtt;
                              const fmtColors = { vtt: '#3b82f6', srt: '#8b5cf6', txt: '#f59e0b' };
                              const allFormats = ['vtt', 'srt', 'txt'];

                              const handleConvert = (fmt, langCode, tapeNum) => {
                                setCleanupRunning(true);
                                const toastId = `convert-${fmt}-${langCode}-${tapeNum}`;
                                pushToast(toastId, 'running', `Creating ${fmt.toUpperCase()} for ${langCode.toUpperCase()} Tape ${tapeNum}…`);
                                axios.post(`${BACKEND_URL}/translate/convert/${encodeURIComponent(cleanupData.root)}?fmt=${fmt}&lang=${langCode}&tape=${tapeNum}`)
                                  .then(r => {
                                    const count = r.data?.files_written?.length || 0;
                                    pushToast(toastId, count ? 'success' : 'error', count ? `Created ${count} ${fmt.toUpperCase()} file(s)` : `No VTT source found`);
                                    loadCleanup();
                                  })
                                  .catch(err => pushToast(toastId, 'error', `Failed: ${err?.response?.data?.detail || err.message}`))
                                  .finally(() => setCleanupRunning(false));
                              };

                              return (
                                <React.Fragment key={l}>
                                  {li === dividerAfterIndex + 1 && otherLangs.length > 0 && (
                                    <td style={{ ...cellStyle, padding: '4px 0', width: '3px', minWidth: '3px' }}>
                                      <div style={{ width: '2px', height: '100%', minHeight: '20px', background: theme.border, margin: '0 auto', borderRadius: '1px' }} />
                                    </td>
                                  )}
                                  <td style={{ ...cellStyle, padding: '4px 2px' }}>
                                    {hasFile ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                        {/* Format badges — show all 3 if VTT exists */}
                                        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                          {allFormats.map(fmt => {
                                            const exists = existingFormats.includes(fmt);
                                            const canCreate = !exists && hasVtt && fmt !== 'vtt';
                                            const canRecreate = exists && hasVtt && fmt !== 'vtt' && canEdit;
                                            const isClickable = canEdit && (canCreate || canRecreate);
                                            return (
                                              <span
                                                key={fmt}
                                                role={isClickable ? 'button' : undefined}
                                                tabIndex={isClickable ? 0 : undefined}
                                                title={canCreate ? `Create ${fmt.toUpperCase()} from VTT` : canRecreate ? `Recreate ${fmt.toUpperCase()} from VTT` : exists ? fmt.toUpperCase() : ''}
                                                onClick={isClickable ? () => handleConvert(fmt, l, tape.tape_number || 1) : undefined}
                                                onKeyDown={e => { if (isClickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleConvert(fmt, l, tape.tape_number || 1); } }}
                                                style={{
                                                  padding: '1px 4px', borderRadius: '3px', fontSize: '0.55rem', fontWeight: 700,
                                                  background: exists ? (fmtColors[fmt] || theme.textSub) : (hasVtt && fmt !== 'vtt' ? theme.border : 'transparent'),
                                                  color: exists ? '#fff' : (hasVtt && fmt !== 'vtt' ? theme.textSub : 'transparent'),
                                                  lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.02em',
                                                  cursor: isClickable ? 'pointer' : 'default',
                                                  opacity: exists ? 1 : (hasVtt && fmt !== 'vtt' ? 0.5 : 0),
                                                  transition: 'opacity 0.15s',
                                                }}
                                              >{fmt}</span>
                                            );
                                          })}
                                        </div>
                                        {/* Last modified timestamp */}
                                        {langInfo.latest_modified && (
                                          <span style={{ fontSize: '0.48rem', color: theme.textSub, opacity: 0.7, lineHeight: 1, whiteSpace: 'nowrap' }}
                                            title={new Date(langInfo.latest_modified).toLocaleString()}>
                                            {(() => {
                                              const d = new Date(langInfo.latest_modified);
                                              const now = new Date();
                                              const diffMs = now - d;
                                              const diffMins = Math.floor(diffMs / 60000);
                                              if (diffMins < 1) return 'just now';
                                              if (diffMins < 60) return `${diffMins}m ago`;
                                              const diffHrs = Math.floor(diffMins / 60);
                                              if (diffHrs < 24) return `${diffHrs}h ago`;
                                              const diffDays = Math.floor(diffHrs / 24);
                                              if (diffDays < 7) return `${diffDays}d ago`;
                                              return `${d.getMonth()+1}/${d.getDate()}`;
                                            })()}
                                          </span>
                                        )}
                                        {/* Delete button (non-source, editors only) */}
                                        {canEdit && !isSource && (
                                          <button
                                            title={`Remove ${langNames[l] || l} for ${tape.tape_number > 0 ? 'Tape ' + tape.tape_number : tape.base}`}
                                            onClick={async () => {
                                              const fileNames = langInfo.files.map(f => f.filename).join(', ');
                                              if (!await confirm({
                                                message: `Remove ${langNames[l] || l} translation for ${tape.tape_number > 0 ? 'Tape ' + tape.tape_number : tape.base}?\n\nFiles: ${fileNames}\nFiles will be versioned to VTT_VERSIONS/ first.`,
                                                title: 'Remove translation?',
                                                confirmLabel: 'Remove',
                                                destructive: true,
                                              })) return;
                                              setCleanupRunning(true);
                                              Promise.all(langInfo.files.map(f =>
                                                axios.delete(`${BACKEND_URL}/translate/stale/${encodeURIComponent(cleanupData.root)}?file=${encodeURIComponent(f.filename)}`)
                                              ))
                                                .then(() => { loadCleanup(); if (onRefresh) onRefresh(); })
                                                .catch(() => {})
                                                .finally(() => setCleanupRunning(false));
                                            }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#ef4444', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                          ><Icon icon={Trash2} size={11} color='#ef4444' /></button>
                                        )}
                                        {isSource && <span style={{ fontSize: '0.55rem', color: '#3b82f6', fontWeight: 600 }}>SRC</span>}
                                      </div>
                                    ) : (
                                      <span style={{ color: theme.textSub, opacity: 0.3 }}>—</span>
                                    )}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Bulk format conversion buttons */}
                    {canEdit && (
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center', margin: '8px 0' }}>
                        {[['srt', '#8b5cf6', 'SRT'], ['txt', '#f59e0b', 'TXT']].map(([fmt, color, label]) => (
                          <button
                            key={fmt}
                            disabled={cleanupRunning}
                            onClick={async () => {
                              if (!await confirm({
                                message: `Create ${label} files for ALL VTTs in this project?\n\nExisting ${label} files will be overwritten.`,
                                title: `Create all ${label} files?`,
                                confirmLabel: 'Create',
                                destructive: false,
                              })) return;
                              setCleanupRunning(true);
                              const toastId = `convert-all-${fmt}`;
                              pushToast(toastId, 'running', `Creating all ${label} files…`);
                              axios.post(`${BACKEND_URL}/translate/convert/${encodeURIComponent(cleanupData.root)}?fmt=${fmt}`)
                                .then(r => {
                                  const count = r.data?.files_written?.length || 0;
                                  pushToast(toastId, count ? 'success' : 'error', count ? `Created ${count} ${label} file(s)` : `No VTT source files found`);
                                  loadCleanup();
                                })
                                .catch(err => pushToast(toastId, 'error', `Failed: ${err?.response?.data?.detail || err.message}`))
                                .finally(() => setCleanupRunning(false));
                            }}
                            style={{
                              padding: '4px 12px', fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px',
                              border: 'none', background: color, color: '#fff', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: '4px', opacity: cleanupRunning ? 0.5 : 1,
                            }}
                          >
                            Create All {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Translate pills moved to Step 4 above */}

                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: theme.textSub, fontSize: '0.82rem' }}>
                    No VTT files found for this project.
                  </div>
                )}

                {/* Stale DB translations */}
                {stale_db_languages.length > 0 && (
                  <div style={{ background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, padding: '14px' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#ef4444', letterSpacing: '0.04em', marginBottom: '8px' }}>
                      STALE DB TRANSLATIONS ({stale_db_mark_count} marks)
                    </div>
                    <div style={{ fontSize: '0.78rem', color: theme.textSub, marginBottom: '8px' }}>
                      These languages have entries in the database but no VTT files on disk:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                      {stale_db_languages.map(l => (
                        <button key={l} disabled={cleanupRunning} onClick={async () => {
                          if (!canEdit) return;
                          if (!await confirm({
                            message: `Remove all "${(langNames[l] || l).toUpperCase()}" translations from the database?`,
                            title: 'Remove stale translations?',
                            confirmLabel: 'Remove',
                            destructive: true,
                          })) return;
                          setCleanupRunning(true);
                          axios.delete(`${BACKEND_URL}/translate/stale/${encodeURIComponent(cleanupData.root)}?lang=${l}`)
                            .then(() => { loadCleanup(); if (onRefresh) onRefresh(); })
                            .catch(() => {})
                            .finally(() => setCleanupRunning(false));
                        }}
                          style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', cursor: canEdit ? 'pointer' : 'default', opacity: cleanupRunning ? 0.5 : 1 }}
                        >
                          {(getLanguageMeta(l)?.label || l).toUpperCase()} ×
                        </button>
                      ))}
                      {canEdit && stale_db_languages.length > 1 && (
                        <button
                          disabled={cleanupRunning}
                          onClick={async () => {
                            const names = stale_db_languages.map(l => (getLanguageMeta(l)?.label || l).toUpperCase()).join(', ');
                            if (!await confirm({
                              message: `Remove ALL stale translations from the database?\n\nLanguages: ${names}\nTotal: ${stale_db_mark_count} marks`,
                              title: 'Clear all stale translations?',
                              confirmLabel: 'Clear All',
                              destructive: true,
                            })) return;
                            setCleanupRunning(true);
                            Promise.all(stale_db_languages.map(l =>
                              axios.delete(`${BACKEND_URL}/translate/stale/${encodeURIComponent(cleanupData.root)}?lang=${l}`)
                            ))
                              .then(() => { loadCleanup(); if (onRefresh) onRefresh(); })
                              .catch(() => {})
                              .finally(() => setCleanupRunning(false));
                          }}
                          style={{ padding: '4px 14px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700, background: '#991b1b', color: '#fff', border: 'none', cursor: 'pointer', opacity: cleanupRunning ? 0.5 : 1 }}
                        >
                          Clear All Stale
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Legend */}
                <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.68rem', color: theme.textSub }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[['vtt', '#3b82f6'], ['srt', '#8b5cf6'], ['txt', '#f59e0b']].map(([fmt, bg]) => (
                      <span key={fmt} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ padding: '1px 4px', borderRadius: '3px', fontSize: '0.55rem', fontWeight: 700, background: bg, color: '#fff', textTransform: 'uppercase' }}>{fmt}</span>
                      </span>
                    ))}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ padding: '1px 4px', borderRadius: '3px', fontSize: '0.55rem', fontWeight: 700, background: theme.border, color: theme.textSub, opacity: 0.5, textTransform: 'uppercase' }}>srt</span> = Creatable
                    </span>
                    <span><span style={{ color: '#3b82f6', fontWeight: 600, fontSize: '0.55rem' }}>SRC</span> = Source</span>
                    <span><span style={{ opacity: 0.3 }}>—</span> No files</span>
                  </div>
                </div>

                {/* VTT Actions + VTT Versions moved to Step 3 above */}

                {/* ── VTT Versions ── */}
                {vttVersions.length > 0 && (
                  <div style={{ background: theme.card, borderRadius: '10px', border: `1px solid ${theme.border}`, padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: theme.text, letterSpacing: '0.04em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Icon icon={History} size={12} color="#8b5cf6" />
                      VTT VERSIONS ({vttVersions.length})
                    </div>
                    <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      {vttVersions.map((v, i) => (
                        <button key={i} onClick={() => onRestoreVersion && onRestoreVersion(v)}
                          style={{ width: '100%', padding: '6px 8px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.62rem', color: theme.text }}
                        >
                          <Icon icon={RotateCcw} size={10} color="#8b5cf6" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700 }}>{v.language_label} ({v.language?.toUpperCase()})</div>
                            <div style={{ fontSize: '0.52rem', color: theme.textSub }}>{v.timestamp} · {v.size_bytes ? `${(v.size_bytes / 1024).toFixed(0)} KB` : ''}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        ) : panelView === 'glossary' ? (
          /* ── Glossary view ── */
          (() => {
            const currentRoot = getProjectRoot(selectedVideo);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Search */}
                <Input
                  theme={theme}
                  placeholder={t('glossary.search')}
                  value={glossarySearch}
                  onChange={e => setGlossarySearch(e.target.value)}
                  style={{ marginBottom: '2px' }}
                />

                {/* Add rule */}
                <GlossaryAddRule
                  theme={theme} BACKEND_URL={BACKEND_URL}
                  currentRoot={currentRoot} refreshGlossary={refreshGlossary} t={t}
                />

                {/* Batch actions */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Button
                    theme={theme}
                    variant="success"
                    size="sm"
                    icon={RefreshCw}
                    onClick={() => {
                      axios.post(`${BACKEND_URL}/glossary/apply`, null, { params: { project_root: currentRoot } })
                        .then(res => { pushToast('gl-apply', 'success', `Updated ${res.data.updated_rows} rows`); refreshMarks(); refreshGlossary(); });
                    }}
                    style={{ flex: 1 }}
                  >
                    {t('glossary.applyAll').toUpperCase()}
                  </Button>
                  <Button
                    theme={theme}
                    variant="accent"
                    size="sm"
                    icon={RotateCcw}
                    onClick={async () => {
                      if (!await confirm({
                        message: 'Revert all auto-fixed pending marks for this project?',
                        title: 'Revert auto-fixes?',
                        confirmLabel: 'Revert',
                        destructive: true,
                      })) return;
                      axios.post(`${BACKEND_URL}/glossary/undo`, null, { params: { project_root: currentRoot } })
                        .then(res => { pushToast('gl-undo', 'success', `Reverted ${res.data.reverted_rows} rows`); refreshMarks(); });
                    }}
                    style={{ flex: 1 }}
                  >
                    {t('glossary.undoAuto').toUpperCase()}
                  </Button>
                </div>

                {/* Sort controls */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['misspelling', 'correction'].map(k => (
                    <button key={k} onClick={() => setSortConfig(prev => ({ key: k, direction: prev.key === k && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                      style={{ flex: 1, padding: '4px', fontSize: '0.62rem', background: sortConfig.key === k ? (theme.activeBorder || theme.glossaryAccent) : theme.card, color: sortConfig.key === k ? contrastText(theme.activeBorder || theme.glossaryAccent) : theme.text, border: 'none', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                      {k.toUpperCase()}
                      {sortConfig.key === k && <Icon icon={sortConfig.direction === 'asc' ? ChevronUp : ChevronDown} size={10} />}
                    </button>
                  ))}
                </div>

                {/* Rule list — grouped */}
                {['project', 'global'].map(scope => {
                  const scopeRules = processedGlossary.filter(g =>
                    scope === 'global' ? g.project_root === '' : g.project_root === currentRoot
                  );
                  if (scopeRules.length === 0) return null;
                  return (
                    <div key={scope}>
                      <div style={{ fontSize: '0.62rem', fontWeight: 700, color: scope === 'global' ? (theme.accent || '#ef4444') : (theme.glossaryAccent || '#6366f1'), letterSpacing: '0.08em', padding: '6px 0 3px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Icon icon={scope === 'global' ? Globe : Folder} size={10} />
                        {scope === 'global' ? t('glossary.globalRules') : t('glossary.projectRules')}
                        <span style={{ fontSize: '0.55rem', color: theme.textSub, fontWeight: 400 }}>({scopeRules.length})</span>
                      </div>
                      {scopeRules.map(g => (
                        <div key={g.id} style={{ display: 'flex', background: theme.card, padding: '6px 8px', marginBottom: '3px', borderRadius: '6px', fontSize: '0.72rem', alignItems: 'center', borderLeft: `3px solid ${scope === 'global' ? (theme.accent || '#ef4444') : (theme.glossaryAccent || '#6366f1')}` }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ color: theme.accent || '#ef4444' }}>{g.misspelling}</span>
                            <span style={{ color: theme.textSub }}> → </span>
                            <span style={{ color: theme.text }}>{g.correction}</span>
                          </div>
                          <button onClick={() => axios.delete(`${BACKEND_URL}/glossary/${g.id}`).then(refreshGlossary)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}>
                            <Icon icon={Trash2} size={11} color='#ef4444' />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {processedGlossary.length === 0 && (
                  <div style={{ color: theme.textSub, fontSize: '0.72rem', textAlign: 'center', padding: '20px' }}>
                    {t('glossary.noRules')}
                  </div>
                )}
              </div>
            );
          })()
        ) : historyOpen ? (
          /* ── Edit History view ── */
          historyLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSub }}>
              <Icon icon={Loader} size={16} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : historyItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSub, fontSize: '0.82rem' }}>
              No edits recorded yet for this project.
            </div>
          ) : (() => {
            // Build dynamic filter pills from unique field names
            const fieldCounts = {};
            historyItems.forEach(h => {
              const f = h.field || 'unknown';
              fieldCounts[f] = (fieldCounts[f] || 0) + 1;
            });
            const fieldKeys = Object.keys(fieldCounts).sort();
            const filtered = historyFieldFilter === 'all'
              ? historyItems
              : historyItems.filter(h => h.field === historyFieldFilter);

            return (
              <>
                {/* Dynamic filter pills */}
                <div style={{ display: 'flex', background: theme.card, borderRadius: '8px', padding: '2px', border: `1px solid ${theme.border}`, marginBottom: '10px', gap: '2px', flexWrap: 'wrap' }}>
                  <button onClick={() => setHistoryFieldFilter('all')}
                    style={{ padding: '4px 8px', fontSize: '0.65rem', fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer',
                      background: historyFieldFilter === 'all' ? '#7c3aed' : 'transparent',
                      color: historyFieldFilter === 'all' ? '#fff' : theme.textSub }}>
                    ALL ({historyItems.length})
                  </button>
                  {fieldKeys.map(f => (
                    <button key={f} onClick={() => setHistoryFieldFilter(f)}
                      style={{ padding: '4px 8px', fontSize: '0.65rem', fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer',
                        background: historyFieldFilter === f ? '#7c3aed' : 'transparent',
                        color: historyFieldFilter === f ? '#fff' : theme.textSub }}>
                      {f.replace('translation.', '').toUpperCase()} ({fieldCounts[f]})
                    </button>
                  ))}
                </div>

                {/* History cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {filtered.map(h => (
                    <div key={h.id} style={{
                      padding: '10px 12px', borderRadius: '10px', background: theme.card,
                      border: `1px solid ${theme.border}`,
                    }}>
                      {/* Edit diff */}
                      <div style={{ fontSize: '0.75rem', lineHeight: 1.5, marginBottom: '6px' }}>
                        {h.action === 'revert' && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 7px', borderRadius: '8px', fontSize: '0.58rem', fontWeight: 700, background: '#8b5cf620', color: '#8b5cf6', border: '1px solid #8b5cf630', marginBottom: '4px' }}>
                            <Icon icon={RotateCcw} size={9} /> Restored
                          </span>
                        )}
                        <div style={{ color: '#ef4444', textDecoration: 'line-through', opacity: 0.7, marginBottom: '2px', wordBreak: 'break-word' }}>
                          {h.old_value ? (h.old_value.length > 150 ? h.old_value.slice(0, 150) + '…' : h.old_value) : '(empty)'}
                        </div>
                        <div style={{ color: theme.success || '#34c759', wordBreak: 'break-word' }}>
                          {h.new_value ? (h.new_value.length > 150 ? h.new_value.slice(0, 150) + '…' : h.new_value) : '(empty)'}
                        </div>
                      </div>
                      {/* Metadata line */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: theme.textSub, opacity: 0.8, display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700 }}>{h.field}</span>
                          <span>·</span>
                          {h.user_name && <><span>{h.user_name}</span><span>·</span></>}
                          <span>{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                          {h.video_id && <><span>·</span><span>{h.video_id}</span></>}
                        </div>
                        <button
                          onClick={() => revertEdit(h.id)}
                          style={{
                            background: 'none', border: `1px solid ${theme.border}`, borderRadius: '5px',
                            padding: '3px 8px', cursor: 'pointer', color: theme.textSub,
                            display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.62rem', fontWeight: 600, flexShrink: 0,
                          }}
                        >
                          <Icon icon={RotateCcw} size={10} />Undo
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()
        ) : (
          /* ── Unified view: all VTT blocks ── */
          unifiedLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSub, fontSize: theme.fsOriginalText }}>
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>
                <Icon icon={Loader} size={18} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
              Loading unified transcript…
            </div>
          ) : unifiedError ? (
            <LoadFailure theme={theme} what={`the transcript for Tape ${selectedPart}`} onRetry={loadUnified} />
          ) : unifiedBlocks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: theme.textSub, fontSize: theme.fsOriginalText, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <Icon icon={Film} size={12} color={theme.textSub} /> No subtitle blocks found for Tape {selectedPart}
            </div>
          ) : ((() => {
            // When searching across all parts, use server-side search results
            if (searchResults !== null && searchTerm.trim().length >= 2) {
              return searchResults;
            }
            let filtered = unifiedFilter === 'all' ? unifiedBlocks
              // Cues where the two diarization engines disagree — the review list.
              : unifiedFilter === 'disagree' ? unifiedBlocks.filter(b => b.speaker && b.speaker_alt && b.speaker !== b.speaker_alt)
              : unifiedFilter === 'edited' ? unifiedBlocks.filter(b => b.has_mark || (b.edited_text && b.edited_text.trim() !== b.original_text?.trim()))
              : unifiedFilter === 'pending' ? unifiedBlocks.filter(b => b.has_mark && b.status === 'pending')
              : unifiedFilter === 'flagged' ? unifiedBlocks.filter(b => b.flagged)
              : unifiedFilter === 'qa' ? unifiedBlocks.filter(b => b.qa_suggestion)
              : unifiedFilter === 'assigned' ? unifiedBlocks.filter(b => b.assigned_to)
              : unifiedFilter === 'mine' ? unifiedBlocks.filter(b => b.assigned_to === currentUserId || (hasRangeAssignments && isInAssignedRange(b.timestamp_start ?? b.start ?? 0)))
              : unifiedFilter === 'reviewed' ? unifiedBlocks.filter(b => b.status === 'completed')
              : unifiedBlocks;
            if (searchTerm.trim()) {
              const q = searchTerm.toLowerCase();
              filtered = filtered.filter(b =>
                (b.original_text || '').toLowerCase().includes(q) ||
                (b.edited_text || '').toLowerCase().includes(q) ||
                Object.values(b.translations || {}).some(t => (t || '').toLowerCase().includes(q))
              );
            }
            return filtered;
          })()).map((block, idx) => (
            <React.Fragment key={block.id || `u-${idx}`}>
              {idx === 0 && <SpeakerLegend speakerMap={speakerMap} canEdit={canEdit} theme={theme} inputStyle={inputStyle} onRename={renameSpeaker} onSwap={swapSpeakers} onDownload={downloadSpeakers} />}
              <div ref={el => {
                const k = blockKey(block);
                if (el) blockRefsMap.current[k] = el;
                else delete blockRefsMap.current[k];   // filtered out — drop the stale node
              }}>
              {/* Number pill */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px', marginTop: idx > 0 ? '4px' : 0 }}>
                <span style={{
                  width: '24px', height: '24px', borderRadius: '50%',
                  background: blockKey(block) === activeBlockKey ? (theme.accent || '#3b82f6') : theme.sidebar,
                  border: `1.5px solid ${blockKey(block) === activeBlockKey ? (theme.accent || '#3b82f6') : theme.border}`,
                  color: blockKey(block) === activeBlockKey ? '#fff' : theme.textSub,
                  fontSize: '0.62rem', fontWeight: 800,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>{idx + 1}</span>
                {block.search_match && block.part_number !== selectedPart && (
                  <span style={{ fontSize: '0.55rem', fontWeight: 700, color: theme.accent || '#3b82f6', background: `${theme.accent || '#3b82f6'}15`, padding: '1px 6px', borderRadius: '4px', flexShrink: 0 }}>
                    TAPE {block.part_number}
                  </span>
                )}
                <div style={{ flex: 1, height: '1px', background: theme.border }} />
              </div>
              <MarkCard
                m={block}
                speakerCompareMode={speakerCompareMode}
                editingLanguage={editingLanguage}
                visibleLanguages={visibleLanguages}
                sourceLanguage={sourceLanguage || preferredEditingLanguage || editingLanguage}
                vttSourceLanguage={vttSourceLanguage}
                theme={theme}
                inputStyle={inputStyle}
                formatVTTTime={formatVTTTime}
                BACKEND_URL={BACKEND_URL}
                videoRef={videoRef}
                onRefresh={() => {
                  // Refresh marks first, then unified data (so the mark is saved before we re-fetch)
                  onRefresh();
                  setTimeout(() => {
                    axios.get(`${BACKEND_URL}/transcript-unified/${encodeURIComponent(selectedVideo)}`, {
                      params: { part: selectedPart },
                    }).then(res => setUnifiedBlocks(res.data || [])).catch(() => {});
                  }, 300);
                }}
                isActive={blockKey(block) === activeBlockKey}
                editors={editors}
                canEdit={canEdit && isInAssignedRange(block.timestamp_start ?? block.start ?? 0)}
                rangeRestricted={canEdit && hasRangeAssignments && !isInAssignedRange(block.timestamp_start ?? block.start ?? 0)}
                selectedPart={selectedPart}
                onSwitchTape={onSwitchTape}
                userRole={userRole}
                projectRoot={getProjectRoot(selectedVideo)}
                currentUserId={currentUserId}
                speakerMap={speakerMap}
                onReassignSpeaker={reassignCueSpeaker}
                cueWords={cueWords}
                onReassignWord={reassignWord}
              />
            </div>
            </React.Fragment>
          ))
        )}

      </div>
    </div>
  );
});

export default RightColumn;
